/**
 * Authenticated fetch wrapper (Zitadel-only auth).
 *
 * Attaches the Zitadel bearer token (registered by AuthContext) to every
 * request. On 401 it redirects to /login to re-authenticate. No cookies / CSRF /
 * refresh machinery — the Zitadel access token is the sole credential, and token
 * renewal is the OIDC client's concern.
 */

import { getActAsTenant } from "./actAs";

// Bearer-token provider: AuthContext registers a getter so every request carries
// a fresh Zitadel access token. It may be async — the provider refreshes the
// token on demand (refresh-token grant) when the current one has expired, so a
// request never goes out with a stale token and 401s the user into a re-login.
// Kept as a registered getter so fetchClient stays decoupled from the OIDC client.
type BearerProvider = () => string | null | Promise<string | null>;
let bearerProvider: BearerProvider | null = null;
export function setBearerProvider(fn: BearerProvider | null): void {
  bearerProvider = fn;
}

export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);

  const bearer = await bearerProvider?.();
  if (bearer && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${bearer}`);
  }

  // Platform-admin "act as tenant": the backend only honours it for platform
  // admins, so it's safe to send unconditionally when set.
  const actAs = getActAsTenant();
  if (actAs && !headers.has("X-Act-As-Tenant")) {
    headers.set("X-Act-As-Tenant", actAs);
  }

  const res = await fetch(input, { ...init, headers });
  if (res.status === 401) {
    redirectToLogin("session_expired");
  }
  return res;
}

function redirectToLogin(reason: string): void {
  // Don't bounce when we're already on the login/callback surface — the initial
  // /auth/me probe 401s there for an unauthenticated user, and redirecting would
  // loop. AuthContext turns that 401 into an `unauthenticated` state instead.
  const path = window.location.pathname;
  if (path.startsWith("/login") || path.startsWith("/sso/callback")) return;
  sessionStorage.setItem("naas_post_login_redirect", path + window.location.search);
  window.location.assign(`/login?reason=${encodeURIComponent(reason)}`);
}
